import { entityPath, type EntityKind } from './entityRoutes';
import { recordLabel } from './recordId';
import type { ActionLogEntry } from '../services/ActionLogService';
import type { StatusBadgeVariant } from '../components/ui/StatusBadge';

/**
 * Words for the History screen. The API logs a mutation by the URL it hit, so
 * `entity_type` is a path segment ("inbox", "client-invoices") and `action` is
 * create / update / delete. None of that is for a person to read.
 */

interface EntityMeta {
  /** Singular, lower case: "task", "client invoice". */
  noun: string;
  /** The record kind whose detail page the entry can open, where one exists. */
  kind?: EntityKind;
}

const ENTITIES: Record<string, EntityMeta> = {
  inbox: { noun: 'inbox message' },
  tasks: { noun: 'task', kind: 'task' },
  vendors: { noun: 'vendor', kind: 'vendor' },
  vendor_bookings: { noun: 'vendor booking', kind: 'vendorBooking' },
  vendor_categories: { noun: 'vendor category' },
  vendor_ratings: { noun: 'vendor rating' },
  vendor_quotes: { noun: 'vendor quote', kind: 'quote' },
  quotes: { noun: 'quote', kind: 'quote' },
  guests: { noun: 'guest', kind: 'guest' },
  guest_tags: { noun: 'guest tag' },
  events: { noun: 'event', kind: 'event' },
  event_activities: { noun: 'event activity' },
  budget: { noun: 'budget item' },
  budget_variances: { noun: 'budget variance' },
  decor: { noun: 'decor element', kind: 'decorElement' },
  stationery: { noun: 'stationery item', kind: 'stationeryItem' },
  clients: { noun: 'client', kind: 'client' },
  client_invoices: { noun: 'client invoice', kind: 'clientInvoice' },
  client_quotes: { noun: 'client quote', kind: 'clientQuote' },
  team: { noun: 'team member', kind: 'teamMember' },
  users: { noun: 'user' },
  songs: { noun: 'song', kind: 'song' },
  bundles: { noun: 'bundle', kind: 'bundle' },
  venues: { noun: 'venue', kind: 'venue' },
  contributors: { noun: 'contributor', kind: 'contributor' },
  guidelines: { noun: 'guideline', kind: 'guideline' },
  menu: { noun: 'menu item', kind: 'menuItem' },
  calendar: { noun: 'calendar event', kind: 'calendarEvent' },
  shipping_agents: { noun: 'shipping agent', kind: 'shippingAgent' },
  procurement: { noun: 'procurement order', kind: 'procurementOrder' },
  inventory: { noun: 'inventory item', kind: 'inventoryItem' },
  asoebi: { noun: 'asoebi record' },
  communications: { noun: 'message' },
  communication_messages: { noun: 'message' },
  comments: { noun: 'comment' },
  notifications: { noun: 'notification' },
  settings: { noun: 'setting' },
  schedule: { noun: 'schedule item' },
  seating: { noun: 'seating plan' },
  floor_plans: { noun: 'floor plan' },
  products: { noun: 'product' },
  payments: { noun: 'payment', kind: 'payment' },
  settlements: { noun: 'settlement' },
  logistics: { noun: 'logistics record' },
  automation: { noun: 'automation' },
};

const normalise = (type: string): string => type.trim().toLowerCase().replace(/[\s-]+/g, '_');
const words = (text: string): string => text.replace(/[_-]+/g, ' ').trim();
const capitalise = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const ACTIONS: Record<string, { label: string; variant: StatusBadgeVariant }> = {
  create: { label: 'Created', variant: 'success' },
  update: { label: 'Updated', variant: 'info' },
  delete: { label: 'Deleted', variant: 'danger' },
  archive: { label: 'Archived', variant: 'neutral' },
  restore: { label: 'Restored', variant: 'success' },
};

/** "Created", "Updated"; an unknown verb is made readable rather than shown raw. */
export function actionLabel(action: string): string {
  return ACTIONS[action.toLowerCase()]?.label ?? capitalise(words(action)) ?? action;
}

export function actionVariant(action: string): StatusBadgeVariant {
  return ACTIONS[action.toLowerCase()]?.variant ?? 'neutral';
}

/** "Task", "Client invoice", "Inbox message". */
export function entityLabel(entityType: string): string {
  const meta = ENTITIES[normalise(entityType)];
  return capitalise(meta?.noun ?? words(entityType)) || 'Record';
}

/** The record's own number ("TA-042") when the entry names one and the kind is known; otherwise ''. */
export function entityRef(entry: Pick<ActionLogEntry, 'entityType' | 'entityId'>): string {
  const kind = ENTITIES[normalise(entry.entityType)]?.kind;
  return kind && entry.entityId ? recordLabel(kind, entry.entityId) : '';
}

/** Where the entry's record lives, when there is a page for it. */
export function entityLink(entry: Pick<ActionLogEntry, 'entityType' | 'entityId'>): string | undefined {
  const kind = ENTITIES[normalise(entry.entityType)]?.kind;
  return kind ? entityPath(kind, entry.entityId) : undefined;
}

const VERB_PHRASES: Record<string, string> = {
  create: 'Added',
  update: 'Updated',
  delete: 'Deleted',
  archive: 'Archived',
  restore: 'Restored',
};

/** One sentence for the Details column: "Updated task TA-042", "Added vendor". */
export function describeChange(entry: Pick<ActionLogEntry, 'action' | 'entityType' | 'entityId'>): string {
  const verb = VERB_PHRASES[entry.action.toLowerCase()] ?? capitalise(words(entry.action));
  const noun = (ENTITIES[normalise(entry.entityType)]?.noun ?? words(entry.entityType)) || 'record';
  const ref = entityRef(entry);
  return [verb, noun, ref].filter(Boolean).join(' ');
}
